import type {AnchorHTMLAttributes} from 'react';

import styled from '@emotion/styled';

import {LinkBasic} from 'UI/components/action/LinkBasic';

import {transient} from 'Application/configs/emotionTransientProps';

import type {LinkProps} from 'next/link';
import type {WithChildren} from 'types/global';

type LinkPropType = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps;

/**
 * The props for the `InlineLink` component, which renders a styled link inline
 * with text or other content.
 * This interface defines optional alignment and a `testId` for testing
 * and automation support.
 */
interface ComponentProps {
    /**
     * Determines the alignment of the link within its container.
     * Can be set to `'center'`, `'left'`, or `'right'`.
     */
    $align?: 'center' | 'left' | 'right';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'InlineLink'
     */
    testId?: string;
}

/**
 * A styled inline link component that wraps its children with link behavior.
 * The `InlineLink` component is similar to `LinkBasic` but intended for inline
 * usage within text or content blocks. It supports alignment customization
 * through the `$align` prop and allows additional link props to be spread
 * onto the underlying `Wrapper`.
 * A `data-cy` attribute is applied for reliable end-to-end test targeting.
 *
 * @param props           The component props.
 * @param props.$align    Optional alignment for the link (e.g., `'left'`, `'center'`, `'right'`).
 * @param props.children  The content to be wrapped by the inline link, typically text or inline elements.
 * @param props.testId    An optional identifier used for testing, applied as `data-cy`. Defaults to `'InlineLink'`.
 * @param props.linkProps Additional link-related props spread onto the `Wrapper`.
 * @returns A JSX element rendering the inline link with styling and behavior.
 *
 * @example
 * ```tsx
 * <InlineLink href="/terms" $align="left">
 *   Read our terms and conditions
 * </InlineLink>
 * ```
 */
const InlineLink = ({
    $align,
    children,
    testId = 'InlineLink',
    ...linkProps
}: WithChildren<ComponentProps> & LinkPropType) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Wrapper $align={$align} data-cy={testId} {...linkProps}>{children}</Wrapper>
);

InlineLink.displayName = 'InlineLink';

export {InlineLink};

const Wrapper = styled(LinkBasic, transient)`
    color: ${({theme}) => theme.colors.textPrimary};
    font-weight: 500;
    text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 1);
    transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out;

    &:hover {
        text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 0);
        transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out;
    }
`;